// Abas "Conciliação" (planilha × ERP, contrato a contrato) e "Metas e histórico"
// (metas anual/mensal/por setor e os totais de jan–set/2026 vindos do Resumo).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { App as AntApp, Button, Card, Col, Dropdown, Flex, Input, InputNumber, Modal, Row, Segmented, Select, Switch, Table, Tag, Tooltip, Typography } from 'antd';
import { ModalEntregar } from './AbaMes';
import { EtapaEntregueAntes, EtapaSetores, EtapaValores } from './ConciliacaoEtapas';
import { usePerms } from '../../hooks/usePerms';
import * as api from './api';
import type { Conciliacao, LinhaConciliacao } from './api';
import { brl, dataBr, lerLinhasAbaMensal, lerResumo, MESES, nomeDe, ORDEM_SETORES, SETOR_CURTO, SETOR_NOMES } from './regras';

const { Text, Paragraph } = Typography;
const PERM_EDITAR = 'operacional.entregas_mensais.editar';

const textoSetores = (s: Record<string, number> | null | undefined) =>
  Object.entries(s || {}).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]).map(([k, v]) => `${SETOR_CURTO[k] || k} ${Math.round(v)}%`).join(' · ') || '—';

export function AbaConciliacao() {
  const { message, modal } = AntApp.useApp();
  const { pode } = usePerms();
  const podeEditar = pode(PERM_EDITAR);
  const [texto, setTexto] = useState('');
  const [aba, setAba] = useState('');
  const [colar, setColar] = useState(false);
  const [r, setR] = useState<Conciliacao | null>(null);
  const [dados, setDados] = useState<api.AbaConciliacaoDados | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [soDivergencia, setSoDivergencia] = useState(true);
  const [etapas, setEtapas] = useState<api.EtapasDados | null>(null);
  const [detalheParcela, setDetalheParcela] = useState(false);
  // Uma coisa de cada vez (Gustavo, 20/10/2026): 1 valor, 2 entregue antes, 3 setores.
  const [etapa, setEtapa] = useState<'1' | '2' | '3'>(() => {
    try { return (sessionStorage.getItem('op:conciliacao_etapa') as any) || '1'; } catch { return '1'; }
  });
  const mudarEtapa = (v: '1' | '2' | '3') => { setEtapa(v); try { sessionStorage.setItem('op:conciliacao_etapa', v); } catch { /* sem storage */ } };
  const lidas = useMemo(() => lerLinhasAbaMensal(texto), [texto]);

  // A aba abre com a última foto gravada da planilha, conciliada contra o ERP de agora.
  const abrir = useCallback(async () => {
    setCarregando(true);
    try {
      const [d, e] = await Promise.all([api.obterConciliacao(), api.obterEtapas()]);
      setEtapas(e);
      setDados(d);
      setR(d.resumo && d.linhas ? { linhas: d.linhas, resumo: d.resumo } : null);
      if (!d.foto) setColar(true);
    } catch (e) { message.error(`Não carregou a conciliação: ${api.erroDe(e)}`); }
    finally { setCarregando(false); }
  }, [message]);
  useEffect(() => { abrir(); }, [abrir]);

  async function gravar() {
    setCarregando(true);
    try {
      if (podeEditar) { await api.gravarConciliacao(aba.trim() || 'colada', lidas); setTexto(''); setColar(false); await abrir(); }
      else setR(await api.conciliar(lidas));
    } catch (e) { message.error(api.erroDe(e)); }
    finally { setCarregando(false); }
  }

  async function adotar(linhas: LinhaConciliacao[]) {
    let ok = 0;
    for (const l of linhas) {
      if (!l.contrato_id) continue;
      try {
        if (!l.erp?.itens) await api.puxarParcelas(l.contrato_id);
        await api.salvarAbertura(l.contrato_id, l.planilha.entregue_acumulado, `Acumulado da planilha (${l.rotulos.join(' + ')})`);
        ok++;
      } catch (e) { message.error(`${l.codigo}: ${api.erroDe(e)}`); }
    }
    message.success(`${ok} contrato(s) adotado(s) no controle.`);
    await abrir();
  }

  async function cancelarItem(id: string, versao: number, descricao: string) {
    modal.confirm({
      title: 'Cancelar este entregável?', content: `“${descricao}” sai da previsão e do saldo em aberto; o registro fica.`,
      okText: 'Cancelar entregável', okButtonProps: { danger: true }, cancelText: 'Voltar',
      onOk: async () => {
        try { await api.salvarItem({ id, versao, status: 'cancelado' }); message.success('Entregável cancelado.'); await abrir(); }
        catch (e) { message.error(api.erroDe(e)); }
      },
    });
  }

  const pend = dados?.pendencias;
  const linhasTabela = (r?.linhas || []).filter(l => !soDivergencia || l.problemas.length);

  return (
    <div>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Flex gap={12} align="center" wrap>
          {dados?.foto
            ? <Text>Planilha de referência: <b>{dados.foto.aba || 'colada'}</b> · {dados.foto.qtd_linhas} linhas · gravada por {nomeDe(dados.foto.criado_por)} em {dataBr(dados.foto.criado_em)}. Conciliada contra o ERP de agora.</Text>
            : <Text type="secondary">Nenhuma planilha gravada ainda. Cole as linhas de uma aba mensal para a conciliação ficar salva aqui.</Text>}
          <div style={{ flex: 1 }} />
          <Button onClick={() => setColar(c => !c)}>{colar ? 'Fechar colagem' : 'Atualizar com a planilha'}</Button>
          <Button onClick={abrir} loading={carregando}>Recarregar</Button>
        </Flex>
        {colar && (
          <div style={{ marginTop: 12 }}>
            <Paragraph type="secondary" style={{ marginBottom: 8 }}>
              Copie da planilha as linhas de uma aba mensal (ex.: OUTUBRO_26), da coluna <b>Projeto</b> até <b>Equipe</b>, e cole abaixo.
              O sistema casa pelo número do contrato (P26050 = HY26050), soma as linhas do mesmo contrato partidas por setor e compara
              valor total, parcelas, acumulado e divisão por setor com o comercial/financeiro. Vale a divisão do ERP.
            </Paragraph>
            <Flex gap={8} wrap style={{ marginBottom: 8 }}>
              <Input style={{ width: 220 }} placeholder="Nome da aba (ex.: OUTUBRO_26)" value={aba} onChange={e => setAba(e.target.value)} maxLength={80} />
            </Flex>
            <Input.TextArea rows={6} value={texto} onChange={e => setTexto(e.target.value)} placeholder="Cole aqui (Ctrl+V) as linhas copiadas da planilha" style={{ fontFamily: 'monospace', fontSize: 12 }} />
            <Button type="primary" style={{ marginTop: 8 }} disabled={!lidas.length} loading={carregando} onClick={gravar}>
              {podeEditar ? 'Gravar e conciliar' : 'Conciliar'} {lidas.length ? `${lidas.length} linhas` : ''}
            </Button>
          </div>
        )}
      </Card>

      {etapas && (
        <Segmented block style={{ marginBottom: 12 }} value={etapa} onChange={v => mudarEtapa(v as any)}
          options={[
            { value: '1', label: `1 · Valor do contrato — ${etapas.resumo.etapa1.divergente || 0} divergentes` },
            { value: '2', label: `2 · Entregue até ${dataBr(etapas.corte)} — ${etapas.resumo.etapa2.divergente || 0} divergentes` },
            { value: '3', label: `3 · Divisão por setor — ${etapas.resumo.etapa3?.divergente || 0} divergentes` },
          ]} />
      )}
      {etapa === '1' && etapas && <EtapaValores linhas={etapas.etapa1} podeEditar={podeEditar} onMudou={abrir} />}
      {etapa === '2' && etapas && <EtapaEntregueAntes linhas={etapas.etapa2} corte={etapas.corte} podeEditar={podeEditar} onMudou={abrir} />}
      {etapa === '3' && etapas?.etapa3 && <EtapaSetores linhas={etapas.etapa3} podeEditar={podeEditar} onMudou={abrir} />}
      {etapa === '3' && (
        <Flex gap={6} align="center" style={{ margin: '16px 0 8px' }}>
          <Switch size="small" checked={detalheParcela} onChange={setDetalheParcela} />
          <Text type="secondary">Ver detalhe por parcela e a conciliação completa da planilha (avançado)</Text>
        </Flex>
      )}
      {etapa === '3' && detalheParcela && (<>
      {pend?.projetos && pend.projetos.length > 0 && (
        <PendenciasPorProjeto projetos={pend.projetos} podeEditar={podeEditar} onMudou={abrir} onCancelar={cancelarItem} />
      )}

      <Flex gap={12} align="center" wrap style={{ margin: '8px 0 12px' }}>
        {r && <Text>{r.resumo.contratos} contratos · <Text type="danger">{r.resumo.com_problema} com divergência</Text> · {r.resumo.sem_contrato} sem contrato no ERP</Text>}
        {r && <Flex gap={6} align="center"><Switch size="small" checked={soDivergencia} onChange={setSoDivergencia} /><Text>Só com divergência</Text></Flex>}
        <div style={{ flex: 1 }} />
        {r && podeEditar && (
          <Button onClick={() => modal.confirm({
            title: 'Adotar todos os contratos encontrados?',
            content: 'Para cada contrato achado no ERP: cria os entregáveis a partir das parcelas (se ainda não houver) e grava o “Entregue acumulado” da planilha como acumulado de abertura. Nada muda no comercial nem no financeiro.',
            okText: 'Adotar', onOk: () => adotar(r.linhas.filter(l => l.contrato_id)),
          })}>Adotar todos no controle</Button>
        )}
      </Flex>
      {r && (
        <Table<LinhaConciliacao> rowKey={l => l.codigo || l.rotulos[0]} size="small" dataSource={linhasTabela} pagination={false} scroll={{ x: 1400 }}
          columns={[
            { title: 'Planilha', render: (_, l) => <span>{l.rotulos.map(x => <div key={x}>{x}</div>)}</span>, width: 300 },
            { title: 'ERP', render: (_, l) => l.numero_erp ? <span><Text strong>{l.numero_erp}</Text> {l.titulo_erp}</span> : <Tag color="red">não achado</Tag>, width: 240 },
            {
              title: 'Valor total (planilha / ERP)', align: 'right', render: (_, l) => (
                <span>{brl(l.planilha.valor_total)}<br /><Text type={l.diferenca_valor_total ? 'danger' : 'secondary'}>{l.erp ? brl(l.erp.valor_total) : '—'}</Text></span>
              ),
            },
            { title: 'Parcelas · faturado (ERP)', align: 'right', render: (_, l) => l.erp ? <span>{brl(l.erp.parcelas_total)}<br /><Text type="secondary">{brl(l.erp.faturado)}</Text></span> : '—' },
            {
              title: 'Acumulado (planilha / sistema)', align: 'right', render: (_, l) => (
                <span>{brl(l.planilha.entregue_acumulado)}<br /><Text type="secondary">{l.erp ? (l.erp.abertura == null && !l.erp.entregue_sistema ? 'não adotado' : brl((l.erp.abertura || 0) + l.erp.entregue_sistema)) : '—'}</Text></span>
              ),
            },
            { title: 'Setores (planilha / ERP)', render: (_, l) => <span>{textoSetores(l.planilha.setores)}<br /><Text type="secondary">{textoSetores(l.erp?.setores)}</Text></span>, width: 260 },
            { title: 'Divergências', render: (_, l) => l.problemas.length ? l.problemas.map(p => <Tag key={p} color="orange" style={{ whiteSpace: 'normal', marginBottom: 2 }}>{p}</Tag>) : <Tag color="green">ok</Tag>, width: 300 },
            { title: '', render: (_, l) => podeEditar && l.contrato_id && <Button size="small" onClick={() => adotar([l])}>Adotar</Button> },
          ]} />
      )}
      </>)}
    </div>
  );
}

// ── Pendências por projeto: cada parcela com financeiro × controle e as ações ──
// Tudo aqui vale SÓ no controle de entregas: a parcela, a DRE e a Receita por
// setor seguem com o financeiro (decisão do Gustavo, 20/10/2026).
const SETORES_CONTROLE = ORDEM_SETORES.filter(s => s !== 'sem_setor');
const COR_PROBLEMA: Record<string, string> = {
  faturada_pendente: 'gold', cancelada: 'red', sem_parcela: 'purple', valor_diferente: 'orange', divisao_diferente: 'blue',
};
const pctRateio = (r: Record<string, number> | null | undefined) =>
  Object.entries(r || {}).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1])
    .map(([k, v]) => `${SETOR_CURTO[k] || k} ${Math.round(v * 1000) / 10}%`).join(' · ') || '—';

function PendenciasPorProjeto({ projetos, podeEditar, onMudou, onCancelar }: {
  projetos: api.PendenciaProjeto[]; podeEditar: boolean; onMudou: () => Promise<void> | void;
  onCancelar: (id: string, versao: number, descricao: string) => void;
}) {
  const { message, modal } = AntApp.useApp();
  const [soProblema, setSoProblema] = useState(true);
  const [editandoRateio, setEditandoRateio] = useState<api.PendenciaParcela | null>(null);
  const [editandoValor, setEditandoValor] = useState<api.PendenciaParcela | null>(null);
  const [entregando, setEntregando] = useState<api.PendenciaParcela | null>(null);
  const hoje = new Date().toISOString().slice(0, 10);

  const executar = async (fn: () => Promise<any>, ok: string) => {
    try { await fn(); message.success(ok); await onMudou(); } catch (e) { message.error(api.erroDe(e)); }
  };
  const confirmar = (titulo: string, texto: string, fn: () => Promise<any>, ok: string) =>
    modal.confirm({ title: titulo, content: texto, okText: 'Confirmar', cancelText: 'Voltar', onOk: () => executar(fn, ok) });

  const acoesDaParcela = (p: api.PendenciaProjeto, i: api.PendenciaParcela) => {
    const itens: { key: string; label: string; danger?: boolean; onClick: () => void }[] = [];
    if (i.status !== 'cancelado') {
      if (i.rateio_origem === 'controle') itens.push({ key: 'fin', label: 'Usar a divisão do financeiro', onClick: () => executar(() => api.itemRateio(i.id, i.versao, { percentuais: null }), 'Divisão do financeiro adotada no controle.') });
      if (p.setores_planilha) itens.push({ key: 'plan', label: `Usar a divisão da planilha (${pctRateio(p.setores_planilha)})`, onClick: () => executar(() => api.itemRateio(i.id, i.versao, { origem: 'planilha' }), 'Divisão da planilha adotada no controle.') });
      itens.push({ key: 'pct', label: 'Editar % por setor…', onClick: () => setEditandoRateio(i) });
      if (i.parcela_id && i.parcela_valor != null && Math.abs(i.parcela_valor - i.valor) > 1) {
        itens.push({ key: 'vfin', label: `Usar o valor do financeiro (${brl(i.parcela_valor, 2)})`, onClick: () => executar(() => api.itemValorFinanceiro(i.id, i.versao), 'Valor do financeiro adotado no controle.') });
      }
      itens.push({ key: 'val', label: 'Editar valor no controle…', onClick: () => setEditandoValor(i) });
    }
    if (i.status === 'pendente') {
      itens.push({ key: 'antes', label: 'Marcar entregue antes do controle (até 30/09)', onClick: () => confirmar(
        'Marcar como entregue antes do controle?',
        'O valor passa a contar no acumulado de abertura do contrato (não no realizado de outubro). Não cria entrega no mês, não fatura e não libera BM.',
        () => api.itemAntesDoControle(i.id, i.versao), 'Marcado como entregue antes do controle.') });
      itens.push({ key: 'entregar', label: 'Entregar neste mês…', onClick: () => setEntregando(i) });
      itens.push({ key: 'cancelar', label: 'Cancelar entregável', danger: true, onClick: () => onCancelar(i.id, i.versao, i.descricao) });
    }
    if (i.antes_do_controle) {
      itens.push({ key: 'desfazer', label: 'Desfazer "entregue antes do controle"', onClick: () => executar(() => api.itemAntesDoControle(i.id, i.versao, true), 'Voltou a pendente.') });
    }
    return itens;
  };

  return (
    <Card size="small" style={{ marginBottom: 12 }}
      title={`Pendências por projeto (${projetos.length} projetos · ${projetos.reduce((s, p) => s + p.parcelas_com_problema, 0)} parcelas incompatíveis)`}
      extra={<Flex gap={6} align="center"><Switch size="small" checked={soProblema} onChange={setSoProblema} /><Text>Só parcelas com problema</Text></Flex>}>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
        As escolhas aqui valem só no controle de entregas: a parcela no financeiro, a DRE e a Receita por setor não mudam.
        Para mudar a parcela no financeiro, use o Contas a Receber.
      </Text>
      <Table<api.PendenciaProjeto> rowKey="contrato_id" size="small" pagination={false} dataSource={projetos} scroll={{ x: 1100 }}
        expandable={{
          expandedRowRender: p => (
            <Table<api.PendenciaParcela> rowKey="id" size="small" pagination={false} scroll={{ x: 1200 }}
              dataSource={soProblema ? p.itens.filter(i => i.problemas.length) : p.itens}
              columns={[
                {
                  title: 'Parcela / entregável', width: 260, render: (_, i) => (
                    <span style={i.status === 'cancelado' ? { color: '#9aa0a6', textDecoration: 'line-through' } : undefined}>
                      {i.numero_parcela != null && <Text type="secondary">{i.numero_parcela}{i.total_parcelas ? `/${i.total_parcelas}` : ''} · </Text>}{i.descricao}
                      <div>
                        <Tag color={i.status === 'entregue' ? 'green' : i.status === 'cancelado' ? 'default' : 'blue'}>
                          {i.status === 'entregue' ? (i.antes_do_controle ? 'entregue antes do controle' : 'entregue') : i.status}
                        </Tag>
                      </div>
                    </span>
                  ),
                },
                {
                  title: 'Financeiro', align: 'right', render: (_, i) => i.parcela_id ? (
                    <span>{brl(i.parcela_valor, 2)}<br /><Text type="secondary" style={{ fontSize: 12 }}>{i.parcela_nf ? `NF ${i.parcela_nf}` : i.parcela_status}{i.parcela_vencimento ? ` · vence ${dataBr(i.parcela_vencimento)}` : ''}</Text></span>
                  ) : <Tag>sem parcela</Tag>,
                },
                { title: 'Controle', align: 'right', render: (_, i) => <span>{brl(i.valor, 2)}<br /><Text type="secondary" style={{ fontSize: 12 }}>saldo {brl(i.saldo, 2)}</Text></span> },
                {
                  title: 'Divisão no controle', width: 240, render: (_, i) => (
                    <Tooltip title={`Financeiro: ${pctRateio(i.rateio_financeiro)}`}>
                      <span>{pctRateio(i.rateio)} <Tag color={i.rateio_origem === 'controle' ? 'geekblue' : 'default'}>{i.rateio_origem === 'controle' ? 'do controle' : 'do financeiro'}</Tag></span>
                    </Tooltip>
                  ),
                },
                { title: 'Incompatível', width: 230, render: (_, i) => i.problemas.length ? i.problemas.map(x => <Tag key={x.codigo} color={COR_PROBLEMA[x.codigo]} style={{ whiteSpace: 'normal', marginBottom: 2 }}>{x.texto}</Tag>) : <Tag color="green">ok</Tag> },
                {
                  title: '', width: 110, render: (_, i) => {
                    const itens = podeEditar ? acoesDaParcela(p, i) : [];
                    return itens.length ? (
                      <Dropdown trigger={['click']} menu={{ items: itens.map(x => ({ key: x.key, label: x.label, danger: x.danger, onClick: x.onClick })) }}>
                        <Button size="small">Ações ▾</Button>
                      </Dropdown>
                    ) : null;
                  },
                },
              ]} />
          ),
        }}
        columns={[
          { title: 'Projeto', render: (_, p) => <span><Text strong>{p.numero}</Text> {p.titulo}<br /><Text type="secondary" style={{ fontSize: 12 }}>{nomeDe(p.responsavel)}</Text></span> },
          { title: 'Valor (ERP)', dataIndex: 'valor_total', align: 'right', render: v => brl(v) },
          { title: 'Saldo a entregar', dataIndex: 'saldo_a_entregar', align: 'right', render: v => brl(v) },
          { title: 'Entregáveis em aberto', dataIndex: 'itens_em_aberto', align: 'right', render: v => brl(v) },
          { title: 'Diferença', dataIndex: 'diferenca', align: 'right', render: v => (v ? <Tooltip title={v > 0 ? 'Saldo do contrato sem entregável (falta parcela/entregável ou o acumulado está baixo).' : 'Entregáveis em aberto passam do saldo (entregue antes do controle sem nota, aditivo ou acumulado alto).'}><Text type="danger">{brl(v)}</Text></Tooltip> : '—') },
          { title: 'Divisão da planilha', render: (_, p) => <Text type="secondary" style={{ fontSize: 12 }}>{pctRateio(p.setores_planilha)}</Text> },
          { title: 'Incompatíveis', dataIndex: 'parcelas_com_problema', align: 'right', render: v => (v ? <Tag color="orange">{v}</Tag> : '—') },
          {
            title: '', render: (_, p) => podeEditar && (
              <Dropdown trigger={['click']} menu={{
                items: [
                  ...(p.setores_planilha ? [{ key: 'plan', label: 'Usar a divisão da planilha em todas as parcelas', onClick: () => confirmar('Usar a divisão da planilha em todas as parcelas?', `${pctRateio(p.setores_planilha)} — só no controle; o financeiro não muda. As entregas já lançadas passam a usar esta divisão.`, () => api.contratoRateio(p.contrato_id, 'planilha'), 'Divisão da planilha adotada no projeto.') }] : []),
                  { key: 'fin', label: 'Usar a divisão do financeiro em todas as parcelas', onClick: () => confirmar('Voltar à divisão do financeiro em todas as parcelas?', 'Remove as escolhas do controle neste projeto; as entregas já lançadas voltam à divisão do financeiro.', () => api.contratoRateio(p.contrato_id, 'financeiro'), 'Divisão do financeiro adotada no projeto.') },
                ],
              }}>
                <Button size="small">Projeto ▾</Button>
              </Dropdown>
            ),
          },
        ]} />

      {editandoRateio && <ModalRateio item={editandoRateio} onFechar={() => setEditandoRateio(null)} onSalvo={onMudou} />}
      {editandoValor && <ModalValor item={editandoValor} onFechar={() => setEditandoValor(null)} onSalvo={onMudou} />}
      {entregando && <ModalEntregar item={entregando as any} hoje={hoje} onFechar={() => setEntregando(null)} onSalvo={() => { onMudou(); }} />}
    </Card>
  );
}

function ModalRateio({ item, onFechar, onSalvo }: { item: api.PendenciaParcela; onFechar: () => void; onSalvo: () => Promise<void> | void }) {
  const { message } = AntApp.useApp();
  const [pct, setPct] = useState<Record<string, number | null>>(() =>
    Object.fromEntries(SETORES_CONTROLE.map(s => [s, item.rateio?.[s] ? Math.round(item.rateio[s] * 1000) / 10 : null])));
  const soma = Object.values(pct).reduce<number>((a, b) => a + (b || 0), 0);
  async function salvar() {
    try {
      const percentuais = Object.fromEntries(Object.entries(pct).filter(([, v]) => v).map(([k, v]) => [k, v as number]));
      await api.itemRateio(item.id, item.versao, { percentuais });
      message.success('Divisão salva no controle.'); await onSalvo(); onFechar();
    } catch (e) { message.error(api.erroDe(e)); }
  }
  return (
    <Modal open title={`Divisão por setor no controle — ${item.descricao}`} onCancel={onFechar} onOk={salvar} okText="Salvar"
      okButtonProps={{ disabled: Math.abs(soma - 100) > 0.5 }} destroyOnHidden width="94vw" style={{ maxWidth: 560, top: 16 }}>
      <Flex vertical gap={8}>
        <Text type="secondary">Vale só no controle de entregas; a parcela no financeiro continua com: {pctRateio(item.rateio_financeiro)}.</Text>
        {SETORES_CONTROLE.map(s => (
          <Flex key={s} justify="space-between" align="center" gap={8}>
            <Text>{SETOR_NOMES[s]}</Text>
            <InputNumber min={0} max={100} step={5} decimalSeparator="," style={{ width: 120 }} addonAfter="%" value={pct[s] ?? undefined}
              onChange={v => setPct(p => ({ ...p, [s]: v }))} />
          </Flex>
        ))}
        <Text type={Math.abs(soma - 100) > 0.5 ? 'danger' : 'success'}>Soma: {soma.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%</Text>
      </Flex>
    </Modal>
  );
}

function ModalValor({ item, onFechar, onSalvo }: { item: api.PendenciaParcela; onFechar: () => void; onSalvo: () => Promise<void> | void }) {
  const { message } = AntApp.useApp();
  const [valor, setValor] = useState<number | null>(item.valor);
  async function salvar() {
    try {
      await api.salvarItem({ id: item.id, versao: item.versao, valor });
      message.success('Valor salvo no controle.'); await onSalvo(); onFechar();
    } catch (e) { message.error(api.erroDe(e)); }
  }
  return (
    <Modal open title={`Valor no controle — ${item.descricao}`} onCancel={onFechar} onOk={salvar} okText="Salvar" destroyOnHidden width="94vw" style={{ maxWidth: 480, top: 16 }}>
      <Flex vertical gap={8}>
        <Text type="secondary">Muda só o valor deste entregável no controle de entregas. {item.parcela_id ? `No financeiro a parcela vale ${brl(item.parcela_valor, 2)}; para mudar lá, use o Contas a Receber.` : ''}</Text>
        <InputNumber min={0} decimalSeparator="," style={{ width: 220 }} value={valor ?? undefined} onChange={v => setValor(v)} />
        {item.entregue_valor > 0 && <Text type="secondary">Já entregue neste item: {brl(item.entregue_valor, 2)} (o valor não pode ficar abaixo disso).</Text>}
      </Flex>
    </Modal>
  );
}

export function AbaMetas() {
  const { message } = AntApp.useApp();
  const { pode } = usePerms();
  const podeEditar = pode(PERM_EDITAR);
  const [ano, setAno] = useState(new Date().getFullYear());
  const [painel, setPainel] = useState<api.Painel | null>(null);
  const [anual, setAnual] = useState<number | null>(null);
  const [superAnual, setSuperAnual] = useState<number | null>(null);
  const [mensal, setMensal] = useState<(number | null)[]>(Array(12).fill(null));
  const [porSetor, setPorSetor] = useState<Record<string, number | null>>({});
  const [textoResumo, setTextoResumo] = useState('');
  // Só meses antes do controle no sistema (o Resumo traz o ano inteiro).
  const historicoLido = useMemo(() => lerResumo(textoResumo, ano)
    .filter(h => !painel || h.competencia < painel.inicio_controle), [textoResumo, ano, painel]);

  const carregar = async (a: number) => {
    try {
      const p = await api.obterPainel(a);
      setPainel(p);
      const geral = p.metas.find(m => m.mes == null && !m.setor);
      setAnual(geral?.valor ?? null); setSuperAnual(geral?.supermeta ?? null);
      setMensal(Array.from({ length: 12 }, (_, i) => p.metas.find(m => m.mes === i + 1 && !m.setor)?.valor ?? null));
      setPorSetor(Object.fromEntries(ORDEM_SETORES.filter(s => s !== 'sem_setor').map(s => [s, p.metas.find(m => m.mes == null && m.setor === s)?.valor ?? null])));
    } catch (e) { message.error(api.erroDe(e)); }
  };
  useEffect(() => { carregar(ano); }, [ano]); // eslint-disable-line react-hooks/exhaustive-deps

  async function salvar() {
    const metas: { mes: number | null; setor: string | null; valor: number; supermeta?: number | null }[] = [];
    if (anual) metas.push({ mes: null, setor: null, valor: anual, supermeta: superAnual });
    mensal.forEach((v, i) => { if (v) metas.push({ mes: i + 1, setor: null, valor: v }); });
    Object.entries(porSetor).forEach(([s, v]) => { if (v) metas.push({ mes: null, setor: s, valor: v }); });
    try { await api.salvarMetas(ano, metas); message.success('Metas salvas.'); await carregar(ano); }
    catch (e) { message.error(api.erroDe(e)); }
  }
  async function importarHistorico() {
    try {
      const r = await api.salvarHistorico(historicoLido);
      message.success(`${r.gravadas} valores do histórico gravados.`);
      setTextoResumo(''); await carregar(ano);
    } catch (e) { message.error(api.erroDe(e)); }
  }

  const hist = painel?.historico || [];
  const valorHist = (mes: number, tipo: string, setor = 'total') =>
    hist.find(h => h.competencia.startsWith(`${ano}-${String(mes).padStart(2, '0')}`) && h.tipo === tipo && h.setor === setor)?.valor ?? null;
  const mesesHistorico = painel ? Array.from({ length: 12 }, (_, i) => i + 1).filter(m => `${ano}-${String(m).padStart(2, '0')}-01` < painel.inicio_controle) : [];

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} xl={10}>
        <Card size="small" title={<Flex gap={8} align="center">Metas de entrega <Select size="small" style={{ width: 100 }} value={ano} onChange={setAno} showSearch optionFilterProp="label" options={[ano - 1, ano, ano + 1].map(a => ({ value: a, label: String(a) }))} /></Flex>}
          extra={podeEditar && <Button type="primary" size="small" onClick={salvar}>Salvar metas</Button>}>
          <Flex gap={12} wrap>
            <div><Text>Meta anual</Text><br /><InputNumber style={{ width: 170 }} min={0} decimalSeparator="," disabled={!podeEditar} value={anual ?? undefined} onChange={v => setAnual(v)} /></div>
            <div><Text>Supermeta anual</Text><br /><InputNumber style={{ width: 170 }} min={0} decimalSeparator="," disabled={!podeEditar} value={superAnual ?? undefined} onChange={v => setSuperAnual(v)} /></div>
          </Flex>
          <Text type="secondary" style={{ display: 'block', margin: '12px 0 4px' }}>Meta mensal (vazio = anual ÷ 12{anual ? ` = ${brl(anual / 12)}` : ''})</Text>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 8 }}>
            {mensal.map((v, i) => (
              <div key={i}><Text style={{ fontSize: 12 }}>{MESES[i]}</Text><br />
                <InputNumber size="small" style={{ width: '100%' }} min={0} decimalSeparator="," disabled={!podeEditar} value={v ?? undefined}
                  onChange={x => setMensal(m => m.map((y, j) => (j === i ? x : y)))} /></div>
            ))}
          </div>
          <Text type="secondary" style={{ display: 'block', margin: '12px 0 4px' }}>Meta anual por setor (opcional)</Text>
          <Flex vertical gap={6}>
            {Object.keys(porSetor).map(s => (
              <Flex key={s} justify="space-between" align="center" gap={8}>
                <Text>{SETOR_NOMES[s]}</Text>
                <InputNumber size="small" style={{ width: 170 }} min={0} decimalSeparator="," disabled={!podeEditar} value={porSetor[s] ?? undefined}
                  onChange={x => setPorSetor(p => ({ ...p, [s]: x }))} />
              </Flex>
            ))}
          </Flex>
        </Card>
      </Col>
      <Col xs={24} xl={14}>
        <Card size="small" title="Histórico da planilha (meses antes do controle no sistema)">
          <Table size="small" rowKey="mes" pagination={false} scroll={{ x: 900 }}
            dataSource={mesesHistorico.map(m => ({ mes: m }))}
            columns={[
              { title: 'Mês', dataIndex: 'mes', render: m => MESES[m - 1] },
              { title: 'Baseline', render: (_, x) => brl(valorHist(x.mes, 'baseline')), align: 'right' },
              { title: 'Ajuste', render: (_, x) => brl(valorHist(x.mes, 'ajuste')), align: 'right' },
              { title: 'Realizado', render: (_, x) => brl(valorHist(x.mes, 'realizado')), align: 'right' },
              ...ORDEM_SETORES.filter(s => s !== 'sem_setor').map(s => ({
                title: SETOR_CURTO[s], key: s, align: 'right' as const, render: (_: any, x: { mes: number }) => brl(valorHist(x.mes, 'realizado', s)),
              })),
            ]} />
          {podeEditar && (
            <div style={{ marginTop: 12 }}>
              <Tooltip title="Na aba Resumo, selecione as linhas dos meses (coluna Mês/Trimestre até o último setor) e copie. Linhas de trimestre e total são ignoradas.">
                <Text>Colar do Resumo da planilha ({ano})</Text>
              </Tooltip>
              <Input.TextArea rows={4} value={textoResumo} onChange={e => setTextoResumo(e.target.value)} style={{ fontFamily: 'monospace', fontSize: 12 }}
                placeholder="janeiro/2026	R$ 407.952,00	R$ 460.707,00	R$ 375.000,00	R$ 374.917,48	…" />
              <Flex gap={8} align="center" style={{ marginTop: 8 }}>
                <Button disabled={!historicoLido.length} onClick={importarHistorico}>Gravar histórico ({new Set(historicoLido.map(h => h.competencia)).size} meses)</Button>
                <Text type="secondary">Só meses anteriores a {painel ? MESES[Number(painel.inicio_controle.slice(5, 7)) - 1] : '—'}; o servidor recusa o resto.</Text>
              </Flex>
            </div>
          )}
        </Card>
      </Col>
    </Row>
  );
}
